import { Component, ErrorInfo, ReactNode } from 'react';

interface PropsLimiteErro {
  /** Componentes filhos a serem protegidos */
  children: ReactNode;
  /** Componente de fallback customizado */
  fallback?: ReactNode;
  /** Callback quando um erro é capturado */
  aoCapturarErro?: (erro: Error, infoErro: ErrorInfo) => void;
}

interface EstadoLimiteErro {
  temErro: boolean;
  erro: Error | null;
}

/**
 * Error Boundary para capturar erros de renderização sem quebrar a interface.
 * Requisitos: 5, 11
 *
 * - Captura erros de renderização em componentes filhos
 * - Exibe fallback amigável ao invés de tela branca
 * - Permite retry (tentar novamente)
 * - Registra erros para debugging
 */
class LimiteErro extends Component<PropsLimiteErro, EstadoLimiteErro> {
  constructor(props: PropsLimiteErro) {
    super(props);
    this.state = { temErro: false, erro: null };
  }

  static getDerivedStateFromError(erro: Error): EstadoLimiteErro {
    return { temErro: true, erro };
  }

  componentDidCatch(erro: Error, infoErro: ErrorInfo): void {
    console.error('[LimiteErro] Erro capturado:', erro, infoErro);
    this.props.aoCapturarErro?.(erro, infoErro);
  }

  private tentarNovamente = (): void => {
    this.setState({ temErro: false, erro: null });
  };

  render(): ReactNode {
    if (this.state.temErro) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="limite-erro" role="alert">
          <div className="limite-erro__conteudo">
            <div className="limite-erro__icone" aria-hidden="true">
              ⚠️
            </div>
            <h2 className="limite-erro__titulo">Algo deu errado</h2>
            <p className="limite-erro__mensagem">
              Ocorreu um erro inesperado ao renderizar este componente.
            </p>
            {this.state.erro && (
              <details className="limite-erro__detalhes">
                <summary>Detalhes do erro</summary>
                <pre className="limite-erro__stack">
                  {this.state.erro.message}
                </pre>
              </details>
            )}
            <button
              type="button"
              className="limite-erro__botao"
              onClick={this.tentarNovamente}
              aria-label="Tentar novamente"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LimiteErro;
